import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { AgencyKnowledgeDto, CATEGORY_LABELS } from '../types';
import {
  useAgencyKnowledge,
  useAgencyKnowledgeTags,
  useCreateAgencyKnowledge,
  useDeleteAgencyKnowledge,
  useUpdateAgencyKnowledge,
} from '../queries';
import { AgencyKnowledgeEditor } from './AgencyKnowledgeEditor';

interface Props {
  agencyId: number;
}

export function AgencyKnowledgeList({ agencyId }: Props) {
  const { data: entries = [], isLoading } = useAgencyKnowledge(agencyId);
  const { data: existingTags = [] } = useAgencyKnowledgeTags(agencyId);
  const createMutation = useCreateAgencyKnowledge(agencyId);
  const updateMutation = useUpdateAgencyKnowledge(agencyId);
  const deleteMutation = useDeleteAgencyKnowledge(agencyId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<AgencyKnowledgeDto | null>(null);

  const openCreate = () => {
    setEditEntry(null);
    setEditorOpen(true);
  };

  const openEdit = (entry: AgencyKnowledgeDto) => {
    setEditEntry(entry);
    setEditorOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Eliminar esta entrada?')) return;
    await deleteMutation.mutateAsync(id);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">Conocimiento de producto</Typography>
          <Typography variant="body2" color="text.secondary">
            Información sobre destinos, logística y productos que el copiloto usa para asistir a tus asesores.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nueva entrada
        </Button>
      </Stack>

      {isLoading ? (
        <Typography variant="body2" color="text.secondary">
          Cargando...
        </Typography>
      ) : entries.length === 0 ? (
        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="body1" color="text.secondary" mb={1}>
            Todavía no hay entradas de conocimiento
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Agregá información sobre destinos, logística y productos para que el copiloto asista mejor a tus asesores.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate}>
            Crear primera entrada
          </Button>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {entry.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={CATEGORY_LABELS[entry.category] ?? entry.category}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {entry.tags.slice(0, 4).map((tag) => (
                        <Chip key={tag} label={tag} size="small" sx={{ fontSize: '0.7rem', height: 18 }} />
                      ))}
                      {entry.tags.length > 4 && (
                        <Tooltip title={entry.tags.slice(4).join(', ')}>
                          <Chip
                            label={`+${entry.tags.length - 4}`}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 18 }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entry.isActive ? 'Activo' : 'Inactivo'}
                      size="small"
                      color={entry.isActive ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                      <IconButton size="small" onClick={() => openEdit(entry)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => void handleDelete(entry.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AgencyKnowledgeEditor
        open={editorOpen}
        entry={editEntry}
        existingTags={existingTags}
        onClose={() => setEditorOpen(false)}
        onCreate={async (payload) => {
          await createMutation.mutateAsync(payload);
        }}
        onUpdate={async (id, payload) => {
          await updateMutation.mutateAsync({ id, payload });
        }}
      />
    </Box>
  );
}
